import { FC, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { notificationsService } from '@/services/notifications.service';
import NotificationsPanel from '@/components/NotificationsPanel';

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: FC<SidebarProps> = ({ isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationsService.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silently fail
    }
  }, []);

  // Poll unread count every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Listen for immediate refresh events
  useEffect(() => {
    const handleRefresh = () => fetchUnreadCount();
    window.addEventListener('notification:refresh', handleRefresh);
    return () => window.removeEventListener('notification:refresh', handleRefresh);
  }, [fetchUnreadCount]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLinkClick = () => {
    if (onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar — theme-aware. Posição controlada só pelo template literal:
          mobile fechado = fixed fora da tela; mobile aberto = drawer fixo;
          desktop = sticky acompanhando o scroll. */}
      <aside className={`border-r border-sidebar-border flex flex-col h-screen bg-sidebar-bg transition-all duration-300 z-50 ${
        isCollapsed ? 'w-20' : 'w-64'
      } ${
        isMobileOpen
          ? 'fixed left-0 top-0'
          : 'fixed left-[-100%] lg:left-0 lg:sticky lg:top-0'
      }`}>
      <div className="p-6 flex flex-col h-full">
        <div className="flex items-center gap-3 mb-10">
          <div className="size-8 bg-[#FCB428] rounded-lg flex items-center justify-center shrink-0">
            <span className="text-[#003046] font-bold text-sm leading-none">h.</span>
          </div>
          {!isCollapsed && (
            <div className="flex flex-col">
              <h1 className="text-sidebar-text text-base font-semibold leading-tight">h.lab</h1>
              <span className="text-sidebar-text-muted text-2xs font-bold uppercase tracking-[0.2em]">Inovação</span>
            </div>
          )}
        </div>

        <nav className="flex-1 space-y-2">
          <Link
            to="/"
            onClick={handleLinkClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive('/')
                ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title="Dashboard"
            aria-label="Dashboard"
          >
            <span className="material-symbols-outlined">dashboard</span>
            {!isCollapsed && <span className="font-medium">Dashboard</span>}
          </Link>
          <Link
            to="/trilhas"
            data-tour="trilhas-link"
            onClick={handleLinkClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive('/trilhas') || location.pathname.startsWith('/trilhas/')
                ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title="Trilhas"
            aria-label="Trilhas"
          >
            <span className="material-symbols-outlined">explore</span>
            {!isCollapsed && <span className="font-medium">Trilhas</span>}
          </Link>
          <Link
            to="/ranking"
            onClick={handleLinkClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive('/ranking')
                ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title="Ranking"
            aria-label="Ranking"
          >
            <span className="material-symbols-outlined">leaderboard</span>
            {!isCollapsed && <span className="font-medium">Ranking</span>}
          </Link>
          <Link
            to="/feed"
            onClick={handleLinkClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive('/feed')
                ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title="Feed"
            aria-label="Feed"
          >
            <span className="material-symbols-outlined">dynamic_feed</span>
            {!isCollapsed && <span className="font-medium">Feed</span>}
          </Link>
          <Link
            to="/perfil"
            onClick={handleLinkClick}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive('/perfil')
                ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title="Meu Perfil"
            aria-label="Meu Perfil"
          >
            <span className="material-symbols-outlined">person</span>
            {!isCollapsed && <span className="font-medium">Meu Perfil</span>}
          </Link>

          {/* Assistente "H.Bot" (Frente 3) — destaque de marca, visível a todos
              QUANDO a flag global GENERAL_AGENT_ENABLED está ligada (kill-switch G12). */}
          {user?.general_agent_enabled && (
            <Link
              to="/assistente"
              onClick={handleLinkClick}
              data-testid="sidebar-assistant-link"
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all border ${
                location.pathname.startsWith('/assistente')
                  ? 'bg-[#FCB428]/15 text-[#FCB428] border-[#FCB428]/40'
                  : 'bg-[#FCB428]/5 text-sidebar-text border-[#FCB428]/20 hover:bg-[#FCB428]/15 hover:text-[#FCB428]'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title="Assistente H.Bot"
              aria-label="Assistente H.Bot"
            >
              <span className="material-symbols-outlined">auto_awesome</span>
              {!isCollapsed && (
                <>
                  <span className="font-semibold">Assistente</span>
                  <span className="ml-auto text-2xs font-bold uppercase tracking-wider bg-[#FCB428] text-[#003046] px-1.5 py-0.5 rounded-full">
                    novo
                  </span>
                </>
              )}
            </Link>
          )}

          {user?.agent_feature_enabled && (
            <Link
              to="/agentes"
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname.startsWith('/agentes')
                  ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                  : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title="Agentes"
              aria-label="Agentes"
            >
              <span className="material-symbols-outlined">psychology</span>
              {!isCollapsed && <span className="font-medium">Agentes</span>}
            </Link>
          )}

          {['ADMIN', 'CURATOR'].includes(user?.role ?? '') && (
            <Link
              to="/admin"
              onClick={handleLinkClick}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname.startsWith('/admin')
                  ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                  : 'text-sidebar-text-muted hover:bg-sidebar-hover hover:text-sidebar-text'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title="Admin"
              aria-label="Admin"
            >
              <span className="material-symbols-outlined">admin_panel_settings</span>
              {!isCollapsed && <span className="font-medium">Admin</span>}
            </Link>
          )}
        </nav>

        <div className="mt-auto pt-6 border-t border-sidebar-border space-y-2">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sidebar-text-muted hover:text-sidebar-text w-full transition-all ${isCollapsed ? 'justify-center' : ''} relative`}
              title="Notificações"
              aria-label="Notificações"
            >
              <span className="material-symbols-outlined relative">
                notifications
                {unreadCount > 0 && (
                  <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-2xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
              {!isCollapsed && <span className="font-medium">Notificações</span>}
              {!isCollapsed && unreadCount > 0 && (
                <span className="ml-auto bg-red-500 text-white text-2xs font-bold px-1.5 py-0.5 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            {/* Portal pra document.body: senão o overlay fica preso no stacking
                context do <aside> (z-50) e some atrás das colunas do assistente
                (que também são z-50 e vêm depois no DOM). */}
            {showNotifications &&
              createPortal(
                <div className="fixed inset-0 z-[60]" onClick={() => setShowNotifications(false)}>
                  <div
                    className="absolute bottom-20 left-2 right-2 lg:right-auto"
                    onClick={(e) => e.stopPropagation()}
                    style={
                      typeof window !== 'undefined' && window.innerWidth >= 1024
                        ? { left: isCollapsed ? '5rem' : '16rem' }
                        : undefined
                    }
                  >
                    <NotificationsPanel onClose={() => setShowNotifications(false)} />
                  </div>
                </div>,
                document.body,
              )}
          </div>

          {/* Tema, Configurações e Suporte movidos/removidos — tema agora vive
              em Meu Perfil → Preferências (sub-fase 4 do onboarding). */}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 w-full transition-all ${isCollapsed ? 'justify-center' : ''}`}
            title="Sair"
            aria-label="Sair da plataforma"
          >
            <span className="material-symbols-outlined">logout</span>
            {!isCollapsed && <span className="font-medium">Sair</span>}
          </button>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sidebar-text-muted hover:text-sidebar-text w-full transition-all ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <span className="material-symbols-outlined">
              {isCollapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
            </span>
            {!isCollapsed && <span className="font-medium">Recolher</span>}
          </button>
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
